/**
 * Theme switch.
 *
 * Three settings, not two: `auto` follows the device, which is what most people
 * want and what a two-way toggle cannot express.
 *
 * The state lives in `@modulus/ui/theme`, the same provider the student app
 * uses, so a preference set here behaves identically there — including the
 * `data-theme` stamp that has to beat the media query in both directions. Only
 * the styling is local, because this app is Tailwind and the shared
 * `ThemeToggle` is written against the `mx-` component sheet.
 */

import { useTheme, type ThemePreference } from '@modulus/ui/theme'

const OPTIONS: { value: ThemePreference; label: string }[] = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'auto', label: 'Auto' },
]

export function ThemeToggle() {
  const { preference, setPreference } = useTheme()

  return (
    <div
      role="group"
      aria-label="Theme"
      className="inline-flex w-full gap-0.5 rounded-full bg-raised p-0.5"
    >
      {OPTIONS.map(({ value, label }) => {
        const active = preference === value
        return (
          <button
            key={value}
            type="button"
            aria-pressed={active}
            onClick={() => setPreference(value)}
            className={`flex-1 rounded-full px-3 py-1.5 text-xs font-semibold transition ${
              active ? 'bg-surface text-ink shadow-sm' : 'text-muted hover:text-ink'
            }`}
          >
            {label}
          </button>
        )
      })}
    </div>
  )
}
